import React, { useEffect, useMemo, useRef, useState } from "react";
import { Label, UnstyledButton } from "src/ui";
import "./TinyFormTable.scss";
import { TextField } from "@fluentui/react";
import SpintrDatePicker from "src/spintr/components/SpintrDatePicker";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { localize } from "src/l10n";
import SpintrSpinButton from "./SpintrSpinButton";

interface IProps {
    question: any;
    answer: any;
    disabled: boolean;
    onChange: (question: any) => void;
}

const TinyFormTable = (props: IProps) => {
    const ref = useRef(null);
    const [width, setWidth] = useState(0);

    const initialAnswer = useMemo(() => {
        const result = {
            ...(props.answer || {}),
            questionId: props.question.id
        }

        if (props.question.rows.length > 0) {
            result.rows = props.question.rows.map((r) => {
                return {
                    surveyTableRowId: r.id,
                    columns: props.question.columns.map((c) => {
                        let text: any = "";
    
                        for (const answerRow of (props.answer?.rows || [])) {
                            for (const answerColumn of answerRow.columns) {
                                if (answerRow.surveyTableRowId === r.id &&
                                    answerColumn.surveyTableColumnId === c.id) {
                                    text = answerColumn.text;
                                }
                            }
                        }

                        if (c.type === 6 && !!text) {
                            text = new Date(text);
                        }

                        return {
                            surveyTableColumnId: c.id,
                            text
                        }
                    })
                }
            });
        } else {
            result.rows = (props.answer?.rows || []).map((r) => {
                return {
                    columns: props.question.columns.map((c) => {
                        let text: any = "";
    
                        for (const answerRow of (props.answer?.rows || [])) {
                            for (const answerColumn of answerRow.columns) {
                                if (answerColumn.surveyTableColumnId === c.id) {
                                    text = answerColumn.text;
                                }
                            }
                        }

                        if (c.type === 6 && !!text) {
                            text = new Date(text);
                        }

                        return {
                            surveyTableColumnId: c.id,
                            text
                        }
                    })
                }
            });
        }

        return result;
    }, []);

    const [answer, setAnswer] = useState(initialAnswer);

    useEffect(() => {
        // @ts-ignore
        const observer = new ResizeObserver(([entry]) => {
            setWidth(entry.contentRect.width);
        });

        if (ref.current) observer.observe(ref.current);

        return () => observer.disconnect();
    }, []);

    const columnNames = useMemo(() => {
        const result: string[] = [];

        if (props.question.rows.length > 0) {
            result.push("");
        }

        for (const c of props.question.columns) {
            result.push(c.text);
        }

        return result;
    }, []);

    useEffect(() => {
        props.onChange(answer);
    }, [answer]);

    const updateAnswer = (rowIndex: number, columnIndex: number, newValue: any) => {
        setAnswer({
            ...answer,
            rows: answer.rows.map((r, rIndex) => {
                return {
                    ...r,
                    columns: r.columns.map((c, cIndex) => {
                        if (rIndex === rowIndex && cIndex === columnIndex) {
                            return {
                                ...c,
                                text: newValue
                            }
                        }

                        return c;
                    })
                }
            })
        });
    }

    const className = useMemo(() => {
        const result = ["TinyFormTable-table"];

        if (columnNames.length < 6 && width > 650) {
            result.push("fixed-table");
        }

        return result.join(" ");
    }, [columnNames, width]);

    return (
        <div className="TinyFormTable" ref={ref}>
            {answer.rows.length > 0 && (
                <table className={className}>
                    <thead>
                        <tr className="row">
                            {columnNames.map((name: string, index: number) => {
                                return (
                                    <th className="column" key={index}>
                                        <Label size="body-2" color="mid-grey" weight="medium">{name}</Label>
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {answer.rows.map((r: any, rIndex: number) => {
                            return (
                                <tr className="row" key={rIndex}>
                                    {props.question.rows.length > 0 && (
                                        <td className="column predefined">
                                            <Label size="body-2" color="mid-grey" weight="medium">
                                                {props.question.rows[rIndex].text}
                                            </Label>
                                        </td>
                                    )}
                                    {r.columns.map((c: any, cIndex: number) => {
                                        const column = props.question.columns.find(x => x.id === c.surveyTableColumnId);

                                        return (
                                            <td className={"column type" + column.type} key={cIndex}>
                                                {column.type === 1 && (
                                                    <TextField
                                                        value={c.text}
                                                        disabled={props.disabled}
                                                        aria-required={props.question.isRequired}
                                                        onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, newValue?: string) => {
                                                            updateAnswer(rIndex, cIndex, newValue);
                                                        }}
                                                    />
                                                )}
                                                {column.type === 6 && (
                                                    <SpintrDatePicker
                                                        value={c.text}
                                                        disabled={props.disabled}
                                                        ariaRequired={props.question.isRequired}
                                                        hideTimeSelect={!column.pickTime}
                                                        onChangeHandler={(date: Date) => {
                                                            updateAnswer(rIndex, cIndex, date);
                                                        }}
                                                    />
                                                )}
                                                {column.type === 8 && (
                                                    <SpintrSpinButton
                                                        value={c.text || ""}
                                                        disabled={props.disabled}
                                                        onChange={(newValue?: string) => {
                                                            updateAnswer(rIndex, cIndex, newValue);
                                                        }}
                                                    />
                                                )}
                                            </td>
                                        )
                                    })}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            )}
            {props.question.rows.length === 0 && !props.disabled && (
                <UnstyledButton
                    className="add-button"
                    onClick={() => {
                        setAnswer({
                            ...answer,
                            rows: [
                                ...(answer.rows || []), 
                                {
                                    columns: props.question.columns.map((c) => {
                                        return {
                                            surveyTableColumnId: c.id,
                                            text: ""
                                        }
                                    })
                                }
                            ]
                        });
                    }}
                >
                    <Visage2Icon icon="add" color="light-blue" />
                    <Label size="body-2" color="light-blue">{localize("ADD_ROW")}</Label>
                </UnstyledButton>
            )}
        </div>
    )
}

export default TinyFormTable;